import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Sparkle, Plus, Trash, ClipboardText } from "@phosphor-icons/react";

export default function EvidenceChecklist({ requirementId, hasMeasure, canEdit, aiEnabled }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ artifact: "", owner: "", period: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/requirements/${requirementId}/evidence-checklist`)
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, [requirementId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    const hasAi = items.some((i) => i.source === "ai");
    if (hasAi && !window.confirm("Re-draft the AI checklist? Existing AI items will be replaced (your checked items are kept where they still match). Manual items are untouched.")) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/requirements/${requirementId}/evidence-checklist/generate${hasAi ? "?force=true" : ""}`);
      setItems(data.items || []);
      toast.success(data.created > 0 ? `${hasAi ? "Re-drafted" : "Added"} ${data.created} evidence item(s)` : "Checklist already covers the Measure");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const toggle = async (it) => {
    setItems((xs) => xs.map((x) => x.id === it.id ? { ...x, done: !x.done } : x));
    try { await api.put(`/evidence-checklist/${it.id}`, { done: !it.done }); }
    catch { load(); toast.error("Could not update"); }
  };

  const addItem = async () => {
    if (!draft.artifact.trim()) return;
    try {
      await api.post(`/requirements/${requirementId}/evidence-checklist`, draft);
      setDraft({ artifact: "", owner: "", period: "" }); setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (it) => {
    setItems((xs) => xs.filter((x) => x.id !== it.id));
    try { await api.delete(`/evidence-checklist/${it.id}`); } catch { load(); }
  };

  const doneCount = items.filter((i) => i.done).length;

  return (
    <div className="border border-emerald-200 bg-emerald-50/20" data-testid="evidence-checklist">
      <div className="border-b border-emerald-200 bg-emerald-100/40 px-4 py-3 flex items-center gap-2 flex-wrap">
        <ClipboardText size={16} weight="fill" className="text-emerald-700" />
        <span className="font-display font-bold text-[#071B3E]">Evidence Checklist</span>
        <span className="text-xs text-muted-foreground hidden sm:inline">— artifacts an auditor will expect</span>
        {items.length > 0 && (
          <span data-testid="checklist-progress" className="ml-1 font-mono text-xs bg-emerald-700 text-white px-2 py-0.5">{doneCount}/{items.length}</span>
        )}
        {canEdit && (
          <div className="ml-auto flex items-center gap-2">
            <Button data-testid="checklist-generate-btn" onClick={generate} disabled={generating || !aiEnabled || !hasMeasure}
              title={!hasMeasure ? "Harvest or add a Measure first" : (!aiEnabled ? "AI is turned off for this entity" : "Draft a checklist from the Measure language")}
              size="sm" variant="outline" className="rounded-none">
              <Sparkle size={14} weight="fill" className="mr-1" />{generating ? "Working…" : (items.some((i) => i.source === "ai") ? "Re-draft (AI)" : "Generate from Measure")}
            </Button>
            <Button data-testid="checklist-add-btn" onClick={() => setAdding((v) => !v)} size="sm" variant="outline" className="rounded-none">
              <Plus size={14} className="mr-1" />Add
            </Button>
          </div>
        )}
      </div>

      {adding && (
        <div className="px-4 py-3 border-b border-emerald-100 grid grid-cols-1 sm:grid-cols-[1fr_140px_140px_auto] gap-2 items-center bg-white/60">
          <Input data-testid="checklist-new-artifact" value={draft.artifact} onChange={(e) => setDraft({ ...draft, artifact: e.target.value })} placeholder="Evidence artifact (e.g. Quarterly training completion report)" className="rounded-none h-8 text-sm" />
          <Input data-testid="checklist-new-owner" value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} placeholder="Owner" className="rounded-none h-8 text-sm" />
          <Input data-testid="checklist-new-period" value={draft.period} onChange={(e) => setDraft({ ...draft, period: e.target.value })} placeholder="Period" className="rounded-none h-8 text-sm" />
          <Button data-testid="checklist-new-save" onClick={addItem} size="sm" className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e] h-8">Save</Button>
        </div>
      )}

      {loading ? (
        <div className="px-4 py-4 text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="px-4 py-4 text-sm text-muted-foreground" data-testid="checklist-empty">
          {hasMeasure ? "No checklist yet — click “Generate from Measure” to draft the evidence an auditor will expect." : "Add a Measure to this requirement to auto-generate an evidence checklist."}
        </div>
      ) : (
        <div className="divide-y divide-emerald-100">
          {items.map((it) => (
            <div key={it.id} data-testid={`checklist-item-${it.id}`} className="px-4 py-2.5 flex items-start gap-3">
              <Checkbox data-testid={`checklist-toggle-${it.id}`} checked={!!it.done} onCheckedChange={() => canEdit && toggle(it)} disabled={!canEdit} className="rounded-none mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.artifact}</p>
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  {it.part_label && <span className="text-[10px] font-mono border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5">Part {it.part_label}</span>}
                  {it.owner && <span className="text-[11px] text-muted-foreground">Owner: <span className="font-medium text-foreground/80">{it.owner}</span></span>}
                  {it.period && <span className="text-[11px] text-muted-foreground">Period: <span className="font-medium text-foreground/80">{it.period}</span></span>}
                  {it.source === "ai" && <span className="text-[10px] uppercase tracking-wide text-indigo-600">AI</span>}
                  {it.done && it.done_by && <span className="text-[10px] text-emerald-700">✓ {it.done_by}{it.done_at ? ` · ${String(it.done_at).slice(0, 10)}` : ""}</span>}
                </div>
              </div>
              {canEdit && (
                <button data-testid={`checklist-delete-${it.id}`} onClick={() => remove(it)} className="text-muted-foreground hover:text-red-600 mt-0.5"><Trash size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
